/**
 * CEPI Agents demo replay — a frontend-only, switch-gated replay of one saved
 * prod run, for screen-recorded demos. Spec/plan:
 * docs/superpowers/{specs,plans}/2026-10-20-cepi-agents-demo-replay*.md
 *
 * Off by default. `?demo=1` turns it on (persisted in localStorage), `?demo=0`
 * turns it off. While on, every request for DEMO_WORKSTREAM_ID is answered
 * from the committed fixture and the SSE stream is synthesized; the backend is
 * never called for that id. The report is served from the fixture UNCHANGED.
 *
 * All phase-dependent state is a pure function of two timestamps
 * (createdAt, approvedAt) in localStorage, so a refresh resumes the replay.
 */
import { useEffect, useState } from 'react';
import type {
  ApprovePlanResponse,
  CapabilityCatalog,
  PlanVersionResponse,
  StepSummary,
  StepToolCallsResponse,
  WorkstreamDetail,
  WorkstreamEvent,
  WorkstreamReport,
  WorkstreamSnapshot,
} from '../types/workstream';
import { TERMINAL_WORKSTREAM_STATUSES } from '../types/workstream';
import type { WorkstreamStreamState } from '../hooks/useWorkstreamStream';

/** The real saved prod run the replay is built from. */
export const DEMO_WORKSTREAM_ID = 'b1a53103-9e70-4601-9181-9b2cbbdac9a8';
export const DEMO_SWITCH_KEY = 'cepi:demoReplay';
export const DEMO_TIMELINE_KEY = 'cepi:demoReplay:timeline';

const PLANNING_PHASES = ['understanding', 'probing', 'reviewing', 'finalizing'] as const;
const PHASE_MS = 1500;
const QUEUED_MS = 1000;
const STEP_MS = 1500;
const SYNTHESIS_MS = 2000;
const TICK_MS = 250;

export interface DemoFixture {
  detail: WorkstreamDetail;
  plan: PlanVersionResponse;
  report: WorkstreamReport;
  capabilities: CapabilityCatalog;
  /** Keyed by step ordinal. */
  tool_calls: Record<string, StepToolCallsResponse>;
}

export interface DemoTimeline {
  createdAt: number;
  approvedAt: number | null;
  /** Set once the finished report was opened in a new tab for this replay. */
  openedReport?: boolean;
}

// ---------------------------------------------------------------------------
// Switch
// ---------------------------------------------------------------------------

// Set for this page load when storage is unavailable but the URL asked for it.
let memorySwitch: boolean | null = null;

/** Apply `?demo=1` / `?demo=0` from the URL. Called once before first render. */
export function syncDemoSwitchFromUrl(search: string = window.location.search): void {
  const value = new URLSearchParams(search).get('demo');
  if (value !== '1' && value !== '0') return;
  memorySwitch = value === '1';
  try {
    if (value === '1') localStorage.setItem(DEMO_SWITCH_KEY, '1');
    else localStorage.removeItem(DEMO_SWITCH_KEY);
  } catch { /* storage unavailable: the in-memory switch covers this load */ }
}

export function isDemoReplayEnabled(): boolean {
  try {
    return localStorage.getItem(DEMO_SWITCH_KEY) === '1';
  } catch {
    return memorySwitch === true;
  }
}

export function isDemoWorkstream(id: string | null | undefined): boolean {
  return id === DEMO_WORKSTREAM_ID && isDemoReplayEnabled();
}

// ---------------------------------------------------------------------------
// Timeline + fixture
// ---------------------------------------------------------------------------

// Set when storage is unavailable, so the replay still plays from the start.
let memoryTimeline: DemoTimeline | null = null;

function readTimeline(): DemoTimeline | null {
  try {
    const raw = localStorage.getItem(DEMO_TIMELINE_KEY);
    if (!raw) return memoryTimeline;
    const parsed = JSON.parse(raw) as Partial<DemoTimeline>;
    if (typeof parsed.createdAt !== 'number') return null;
    return {
      createdAt: parsed.createdAt,
      approvedAt: typeof parsed.approvedAt === 'number' ? parsed.approvedAt : null,
      ...(parsed.openedReport === true ? { openedReport: true } : {}),
    };
  } catch {
    return memoryTimeline;
  }
}

function writeTimeline(timeline: DemoTimeline): void {
  memoryTimeline = timeline;
  try { localStorage.setItem(DEMO_TIMELINE_KEY, JSON.stringify(timeline)); } catch { /* memoryTimeline covers this load */ }
}

let fixturePromise: Promise<DemoFixture> | null = null;
export function loadDemoFixture(): Promise<DemoFixture> {
  fixturePromise ??= import('./cepiDemoReplayFixture.json').then(
    (m) => (m as unknown as { default: DemoFixture }).default,
  );
  return fixturePromise;
}

/** Begin a fresh replay and return the workstream id to navigate to. */
export function startDemoReplay(): string {
  writeTimeline({ createdAt: Date.now(), approvedAt: null });
  // The plan-reveal gate is keyed per id and the demo id is fixed: clear it so
  // the staged reveal animates on every replay.
  try { localStorage.removeItem(`cepi:planRevealed:${DEMO_WORKSTREAM_ID}`); } catch { /* ignore */ }
  return DEMO_WORKSTREAM_ID;
}

// ---------------------------------------------------------------------------
// State at a point in time (pure)
// ---------------------------------------------------------------------------

export interface ReplayState {
  snapshot: WorkstreamDetail & WorkstreamSnapshot;
  events: WorkstreamEvent[];
  status: string;
  terminal: boolean;
  frameKey: string;
}

const iso = (ms: number) => new Date(ms).toISOString();

export function replayStateAt(
  fixture: DemoFixture,
  timeline: DemoTimeline | null,
  nowMs: number,
): ReplayState {
  const saved = [...fixture.detail.steps].sort((a, b) => a.ordinal - b.ordinal);
  const n = saved.length;

  // No timeline: a plain visit to the demo id shows the saved run as it was.
  if (!timeline) {
    const snapshot = { ...fixture.detail, steps: saved, is_owner: true, sse_seq: 0 };
    return {
      snapshot, events: [], status: snapshot.status,
      terminal: TERMINAL_WORKSTREAM_STATUSES.has(snapshot.status), frameKey: 'saved',
    };
  }

  const t0 = timeline.createdAt;
  const a = timeline.approvedAt;
  const draftEnd = t0 + PLANNING_PHASES.length * PHASE_MS;
  const runStart = a === null ? null : a + QUEUED_MS;
  const stepsEnd = runStart === null ? null : runStart + n * STEP_MS;
  const synthEnd = stepsEnd === null ? null : stepsEnd + SYNTHESIS_MS;

  let status: string;
  if (nowMs < draftEnd) status = 'drafting';
  else if (a === null || nowMs < a) status = 'awaiting_approval';
  else if (nowMs < runStart!) status = 'queued';
  else if (nowMs < synthEnd!) status = 'running';
  else status = fixture.detail.status;
  const terminal = TERMINAL_WORKSTREAM_STATUSES.has(status);

  const phaseCount = Math.min(
    PLANNING_PHASES.length,
    Math.max(0, Math.floor((nowMs - t0) / PHASE_MS) + 1),
  );
  const events: WorkstreamEvent[] = PLANNING_PHASES.slice(0, phaseCount).map((phase, i) => ({
    seq: i + 1,
    event: 'planning',
    workstream_id: DEMO_WORKSTREAM_ID,
    payload: { phase },
    emitted_at: iso(t0 + i * PHASE_MS),
  }));

  const steps: StepSummary[] = status === 'drafting' || status === 'awaiting_approval'
    ? []
    : saved.map((step, i) => {
      const start = runStart! + i * STEP_MS;
      if (nowMs < start) {
        return { ...step, status: 'pending', started_at: null, completed_at: null };
      }
      if (nowMs < start + STEP_MS) {
        return {
          ...step,
          status: 'running',
          started_at: iso(start),
          completed_at: null,
          ...(step.kind === 'analysis' ? { analysis_phase: 'analysis' as const } : {}),
        };
      }
      return { ...step, started_at: iso(start), completed_at: iso(start + STEP_MS) };
    });

  const approved = a !== null;
  const snapshot = {
    ...fixture.detail,
    status,
    steps,
    is_owner: true,
    cancel_requested: false,
    action_required_reason: null,
    created_at: iso(t0),
    sse_seq: events.length,
    approved_plan_version: approved ? fixture.detail.approved_plan_version : null,
    approved_plan_hash: approved ? fixture.detail.approved_plan_hash : null,
    approved_by: approved ? fixture.detail.approved_by : null,
    approved_at: approved ? iso(a) : null,
    ...(terminal ? {} : { report_generated_at: null, synthesis_status: null, completed_at: null }),
  };
  const frameKey = [status, phaseCount, steps.map((s) => String(s.status)[0]).join('')].join('|');
  return { snapshot, events, status, terminal, frameKey };
}

// ---------------------------------------------------------------------------
// Request router (used by WorkstreamPage's apiGet / apiPost)
// ---------------------------------------------------------------------------

export interface DemoResponse<T> {
  ok: boolean;
  status: number;
  body: T | null;
}

const reply = <T>(status: number, body: unknown): DemoResponse<T> => ({
  ok: status >= 200 && status < 300,
  status,
  body: (body === null ? null : structuredClone(body)) as T | null,
});

/** Answer a request from the fixture, or `null` when it is not a demo request. */
export async function demoApiResponse<T>(
  method: 'GET' | 'POST',
  fullPath: string,
  _payload?: unknown,
): Promise<DemoResponse<T> | null> {
  if (!isDemoReplayEnabled()) return null;
  const path = fullPath.split('?')[0] ?? fullPath;
  const prefix = `/v2/workstreams/${DEMO_WORKSTREAM_ID}`;
  const isCatalog = path === '/v2/workstreams/capabilities'
    && window.location.pathname.includes(DEMO_WORKSTREAM_ID);
  if (!path.startsWith(prefix) && !isCatalog) return null;

  const fixture = await loadDemoFixture();
  const timeline = readTimeline();
  const state = replayStateAt(fixture, timeline, Date.now());
  const rest = path.slice(prefix.length);
  const approved = timeline?.approvedAt != null || timeline === null;
  const demoPlan = () => ({
    ...fixture.plan,
    is_approved: approved,
    is_approvable: !approved,
    available_versions: [fixture.plan.version],
  });

  if (isCatalog) return reply<T>(200, fixture.capabilities);

  if (method === 'GET') {
    if (rest === '') return reply<T>(200, state.snapshot);
    if (rest === '/plan') {
      return state.status === 'drafting' ? reply<T>(404, null) : reply<T>(200, demoPlan());
    }
    if (rest === '/report') {
      return state.terminal ? reply<T>(200, fixture.report) : reply<T>(409, null);
    }
    const toolCalls = /^\/steps\/(\d+)\/tool-calls$/.exec(rest);
    if (toolCalls) {
      const calls = fixture.tool_calls[toolCalls[1]!];
      return calls ? reply<T>(200, calls) : reply<T>(404, null);
    }
    return reply<T>(200, {});
  }

  if (rest === '/approve') {
    const now = Date.now();
    if (timeline && state.status === 'awaiting_approval') {
      writeTimeline({ ...timeline, approvedAt: now });
    }
    const body: ApprovePlanResponse = {
      workstream_id: DEMO_WORKSTREAM_ID,
      status: 'queued',
      version: fixture.plan.version,
      content_hash: fixture.plan.content_hash,
      approved_by: fixture.detail.approved_by,
      approved_at: iso(now),
      steps_materialized: fixture.detail.steps.length,
    };
    return reply<T>(200, body);
  }
  if (rest === '/plan' || rest === '/plan/revise') return reply<T>(200, demoPlan());
  if (rest === '/cancel') {
    return reply<T>(200, { id: DEMO_WORKSTREAM_ID, status: state.status, cancel_requested: false });
  }
  return reply<T>(200, {});
}

// ---------------------------------------------------------------------------
// Stream replacement
// ---------------------------------------------------------------------------

/** Drop-in for the SSE state in `useWorkstreamStream`; `null` id is inert. */
export function useDemoReplayStream(id: string | null): WorkstreamStreamState | null {
  const [state, setState] = useState<WorkstreamStreamState | null>(null);

  useEffect(() => {
    if (!id) {
      setState(null);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    let lastKey: string | null = null;
    // null until the first tick: a replay that is already terminal when the
    // page mounts (refresh, direct visit) must never open a tab.
    let wasTerminal: boolean | null = null;
    void loadDemoFixture().then((fixture) => {
      if (cancelled) return;
      const tick = () => {
        const timeline = readTimeline();
        const next = replayStateAt(fixture, timeline, Date.now());
        if (wasTerminal === false && next.terminal && timeline && !timeline.openedReport) {
          // Persist first so a second tab/effect (StrictMode) cannot repeat it.
          writeTimeline({ ...timeline, openedReport: true });
          try { window.open(`/workstream/${DEMO_WORKSTREAM_ID}/report`, '_blank'); } catch { /* popup blocked */ }
        }
        wasTerminal = next.terminal;
        // Only publish a new object when something visible changed: the page's
        // audit effects depend on the identity of `steps`.
        if (next.frameKey === lastKey) return;
        lastKey = next.frameKey;
        setState({
          snapshot: next.snapshot,
          events: next.events,
          lastEventId: next.events.length,
          connection: 'open',
          terminalStatus: next.terminal ? next.status : null,
          error: null,
          httpStatus: 200,
        });
      };
      tick();
      timer = setInterval(tick, TICK_MS);
    });
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [id]);

  return id ? state : null;
}
